import React, { useState } from 'react';
import { X, Calendar as CalendarIcon, Clock, MapPin, Video, User, Mail, Phone, Send, CheckCircle, Briefcase, Home } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { toast } from 'sonner';
import { propertyData, brandAssets, addInquiry } from '../../data/mock';
import { format } from 'date-fns';

const ScheduleModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [tourType, setTourType] = useState('in-person');
  const [buyerType, setBuyerType] = useState('buyer');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const availableTimes = [
    '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'
  ];

  const handleSubmit = async () => {
    setIsSubmitting(true);

    await new Promise((resolve) => setTimeout(resolve, 1500));

    addInquiry({
      ...formData,
      tourType,
      buyerType,
      preferredDate: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '',
      preferredTime: selectedTime,
      property: propertyData.address,
    });

    setIsSubmitting(false);
    setIsSubmitted(true);
    toast.success('Your viewing request has been submitted. The estate will confirm shortly.');
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setStep(1);
      setSelectedDate(null);
      setSelectedTime('');
      setTourType('in-person');
      setBuyerType('buyer');
      setFormData({ name: '', email: '', phone: '' });
      setIsSubmitted(false);
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="schedule-modal">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-gradient-to-br from-[#1a1f2e] to-[#0d1117] border border-white/10 rounded-2xl p-6 sm:p-8 max-w-lg w-full shadow-2xl animate-in zoom-in-95 duration-300 my-8">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition-colors z-10"
          data-testid="close-modal-btn"
        >
          <X className="w-5 h-5" />
        </button>

        {isSubmitted ? (
          <div className="text-center py-8" data-testid="schedule-success">
            <div className="w-20 h-20 mx-auto bg-green-500/20 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h3 className="text-2xl font-semibold text-white mb-3">Request Submitted</h3>
            <p className="text-white/70 font-light mb-6">
              The estate will review your request and respond within 24-48 hours to confirm your
              {tourType === 'virtual' ? ' virtual' : ' private'} viewing.
            </p>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-left">
              <p className="text-white/60 text-sm mb-2">Your requested time:</p>
              <p className="text-white font-medium">
                {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}
              </p>
              <p className="text-amber-400">{selectedTime}</p>
            </div>
            <Button
              onClick={handleClose}
              className="mt-6 bg-amber-500 hover:bg-amber-600 text-white"
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="mb-6 flex items-center space-x-4">
              <img 
                src={brandAssets.wCrest} 
                alt="William Lane Estate" 
                className="w-12 h-12 object-contain"
              />
              <div>
                <h3 className="text-xl font-semibold text-white">Request Private Viewing</h3>
                <p className="text-white/60 text-sm">{propertyData.brandName}</p>
              </div>
            </div>

            {/* Progress Steps */}
            <div className="flex items-center mb-8">
              {[1, 2, 3].map((s) => (
                <React.Fragment key={s}>
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                      step >= s
                        ? 'bg-amber-500 text-white'
                        : 'bg-white/10 text-white/40'
                    }`}
                  >
                    {s}
                  </div>
                  {s < 3 && (
                    <div className={`flex-1 h-0.5 mx-2 transition-all ${
                      step > s ? 'bg-amber-500' : 'bg-white/10'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Step 1: Tour Type & Buyer Type */}
            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <p className="text-white/80 mb-4">How would you like to view the estate?</p>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => setTourType('in-person')}
                      className={`p-5 rounded-xl border flex flex-col items-center space-y-2 transition-all duration-300 ${
                        tourType === 'in-person'
                          ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                          : 'bg-white/5 border-white/10 text-white/60 hover:border-white/20'
                      }`}
                    >
                      <MapPin className="w-6 h-6" />
                      <span className="font-medium">In-Person</span>
                    </button>
                    <button
                      onClick={() => setTourType('virtual')}
                      className={`p-5 rounded-xl border flex flex-col items-center space-y-2 transition-all duration-300 ${
                        tourType === 'virtual'
                          ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                          : 'bg-white/5 border-white/10 text-white/60 hover:border-white/20'
                      }`}
                    >
                      <Video className="w-6 h-6" />
                      <span className="font-medium">Virtual</span>
                    </button>
                  </div>
                </div>

                <div>
                  <p className="text-white/80 mb-4">I am a...</p>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => setBuyerType('buyer')}
                      className={`p-4 rounded-xl border flex items-center space-x-3 transition-all duration-300 ${
                        buyerType === 'buyer'
                          ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                          : 'bg-white/5 border-white/10 text-white/60 hover:border-white/20'
                      }`}
                    >
                      <Home className="w-5 h-5" />
                      <span className="text-sm font-medium">Private Buyer</span>
                    </button>
                    <button
                      onClick={() => setBuyerType('broker')}
                      className={`p-4 rounded-xl border flex items-center space-x-3 transition-all duration-300 ${
                        buyerType === 'broker'
                          ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                          : 'bg-white/5 border-white/10 text-white/60 hover:border-white/20'
                      }`}
                    >
                      <Briefcase className="w-5 h-5" />
                      <span className="text-sm font-medium">Buyer's Broker</span>
                    </button>
                  </div>
                </div>

                <Button
                  onClick={() => setStep(2)}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                >
                  Continue
                </Button>
              </div>
            )}

            {/* Step 2: Date & Time */}
            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <p className="text-white/80 mb-3 flex items-center">
                    <CalendarIcon className="w-4 h-4 mr-2 text-amber-400" />
                    Select your preferred date
                  </p>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className="w-full p-4 bg-white/5 border border-white/10 rounded-xl text-left text-white hover:border-amber-500/50 transition-colors">
                        {selectedDate ? format(selectedDate, 'EEEE, MMMM d, yyyy') : 'Choose a date'}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#1a1f2e] border-white/10" align="start">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={(date) => date < new Date()}
                        className="text-white"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <p className="text-white/80 mb-3 flex items-center">
                    <Clock className="w-4 h-4 mr-2 text-amber-400" />
                    Select your preferred time
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {availableTimes.map((time) => (
                      <button
                        key={time}
                        onClick={() => setSelectedTime(time)}
                        className={`py-3 px-2 rounded-lg text-sm transition-all ${
                          selectedTime === time
                            ? 'bg-amber-500 text-white'
                            : 'bg-white/5 border border-white/10 text-white/70 hover:border-amber-500/50'
                        }`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex space-x-3">
                  <Button
                    onClick={() => setStep(1)}
                    variant="outline"
                    className="flex-1 border-white/20 text-white hover:bg-white/10"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={() => setStep(3)}
                    disabled={!selectedDate || !selectedTime}
                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50"
                  >
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Contact Info */}
            {step === 3 && (
              <div className="space-y-4">
                <p className="text-white/80 mb-4">Enter your contact information</p>
                
                <div>
                  <label className="block text-white/60 text-sm mb-2">Full Name *</label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                    <Input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter your full name"
                      className="pl-12 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                      data-testid="modal-name-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white/60 text-sm mb-2">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="your@email.com"
                      className="pl-12 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                      data-testid="modal-email-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white/60 text-sm mb-2">Phone</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="(555) 123-4567"
                      className="pl-12 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                      data-testid="modal-phone-input"
                    />
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <Button
                    onClick={() => setStep(2)}
                    variant="outline"
                    className="flex-1 border-white/20 text-white hover:bg-white/10"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={!formData.name || !formData.email || isSubmitting}
                    className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white disabled:opacity-50"
                    data-testid="modal-submit-btn"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Submitting...
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <Send className="w-4 h-4 mr-2" />
                        Request Viewing
                      </span>
                    )}
                  </Button>
                </div>

                <p className="text-white/40 text-xs text-center pt-2">
                  Your information is confidential.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ScheduleModal;
